import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRightIcon, BoltIcon, ShieldCheckIcon, BanknotesIcon } from '@heroicons/react/24/outline';
import { inferenceService, PriceRow, CatalogEntry } from '../services/inferenceApi';
import { filterByCategory, toggleCompare } from '../utils/catalog';
import { redirectToCustomerLogin } from '../lib/sso';
import SiteNav from '../components/SiteNav';
import { CodeCard } from '../components/CodeCard';
import RecentlyAdded from '../components/catalog/RecentlyAdded';
import CategoryTabs from '../components/catalog/CategoryTabs';
import CompareTray from '../components/catalog/CompareTray';
import ModelDetailsModal from '../components/catalog/ModelDetailsModal';
import CompareSheet from '../components/catalog/CompareSheet';
import { captureFocus, restoreFocus, trapFocus } from '../utils/focusTrap';

const SNIPPETS: Record<string, string> = {
  curl: `curl https://cloud.podstack.ai/infer/v1/chat/completions \\
  -H "Authorization: Bearer $PODSTACK_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{"model": "MODEL", "messages": [{"role": "user", "content": "Hello!"}]}'`,
  python: `from openai import OpenAI

client = OpenAI(
    base_url="https://cloud.podstack.ai/infer/v1",
    api_key="PODSTACK_API_KEY",
)
resp = client.chat.completions.create(
    model="MODEL",
    messages=[{"role": "user", "content": "Hello!"}],
)
print(resp.choices[0].message.content)`,
  javascript: `import OpenAI from "openai";

const client = new OpenAI({
  baseURL: "https://cloud.podstack.ai/infer/v1",
  apiKey: process.env.PODSTACK_API_KEY,
});
const resp = await client.chat.completions.create({
  model: "MODEL",
  messages: [{ role: "user", content: "Hello!" }],
});
console.log(resp.choices[0].message.content);`,
};

// Same overlay/focus/Esc wiring as ModelDetailsModal, sized for a side-by-side comparison.
function LandingCompareModal({
  a,
  b,
  currency,
  onClose,
}: {
  a: CatalogEntry;
  b: CatalogEntry;
  currency: string;
  onClose: () => void;
}) {
  const panelRef = useRef<HTMLDivElement>(null);

  // Keep the latest onClose in a ref so the keydown listener below can stay
  // current without depending on `onClose` directly - onClose is a fresh
  // inline closure on every parent render, and depending on it there would
  // tear down/re-run the capture-focus effect on any incidental re-render,
  // restoring focus to the trigger and re-capturing mid-open.
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  // Mount-only: capture the pre-open focus target and restore it on unmount.
  useEffect(() => {
    const previouslyFocused = captureFocus();
    panelRef.current?.focus();
    return () => restoreFocus(previouslyFocused);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onCloseRef.current();
        return;
      }
      if (e.key === 'Tab' && panelRef.current) {
        trapFocus(panelRef.current, e);
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, []);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4"
      role="dialog"
      aria-modal="true"
      aria-label="Compare models"
      onClick={onClose}
    >
      <div
        ref={panelRef}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-3xl rounded-[14px] border border-ps-strong bg-ps-card p-6 pt-14 max-h-[85vh] overflow-y-auto shadow-xl shadow-black/60 focus:outline-none"
      >
        <button
          aria-label="Close"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 shrink-0 p-1 rounded-md text-ps-subtle hover:text-ps-text hover:bg-ps-raised"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" className="w-5 h-5">
            <path d="M6.28 5.22a.75.75 0 0 0-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 1 0 1.06 1.06L10 11.06l3.72 3.72a.75.75 0 1 0 1.06-1.06L11.06 10l3.72-3.72a.75.75 0 0 0-1.06-1.06L10 8.94 6.28 5.22Z" />
          </svg>
        </button>
        <CompareSheet a={a} b={b} currency={currency} />
        <div className="mt-6 flex justify-center">
          <Link to="/login" className="ps-btn">
            Sign in to try these models
          </Link>
        </div>
      </div>
    </div>
  );
}

const InfoIcon = () => (
  <svg viewBox="0 0 20 20" fill="currentColor" className="w-4 h-4">
    <path
      fillRule="evenodd"
      d="M18 10a8 8 0 1 1-16 0 8 8 0 0 1 16 0Zm-7-4a1 1 0 1 1-2 0 1 1 0 0 1 2 0ZM9 9a.75.75 0 0 0 0 1.5h.253a.25.25 0 0 1 .244.304l-.459 2.066A1.75 1.75 0 0 0 10.747 15H11a.75.75 0 0 0 0-1.5h-.253a.25.25 0 0 1-.244-.304l.459-2.066A1.75 1.75 0 0 0 9.253 9H9Z"
      clipRule="evenodd"
    />
  </svg>
);

const Landing = () => {
  const [lang, setLang] = useState<'curl' | 'python' | 'javascript'>('curl');
  const [category, setCategory] = useState('all');
  const [compareSelection, setCompareSelection] = useState<string[]>([]);
  const [detailsEntry, setDetailsEntry] = useState<CatalogEntry | null>(null);
  const [compareOpen, setCompareOpen] = useState(false);

  const { data } = useQuery({
    queryKey: ['public-pricing'],
    queryFn: () => inferenceService.getPublicPricing().then((r) => r.data.models),
  });
  const models: PriceRow[] = data ?? [];

  const { data: catalogData } = useQuery({
    queryKey: ['catalog'],
    queryFn: inferenceService.getCatalog,
  });
  const catalogModels: CatalogEntry[] = catalogData?.models ?? [];
  const currency = catalogData?.currency ?? 'INR';
  const catalogById = new Map(catalogModels.map((m) => [m.id, m]));

  // Pricing rows without a catalog entry only ever show under "All".
  const categoryIds = new Set(filterByCategory(catalogModels, category).map((m) => m.id));
  const displayedModels = category === 'all' ? models : models.filter((m) => categoryIds.has(m.id));

  const handleCompareToggle = (id: string) => {
    setCompareSelection((prev) => toggleCompare(prev, id));
  };

  const compareA = compareSelection[0] ? catalogById.get(compareSelection[0]) : undefined;
  const compareB = compareSelection[1] ? catalogById.get(compareSelection[1]) : undefined;

  return (
    <div className="min-h-screen bg-ps-page text-ps-text">
      <SiteNav getStartedLabel="Get your API key" />

      {/* Hero */}
      <section className="mx-auto max-w-7xl px-4 pb-16 pt-28 sm:px-6 sm:pt-32">
        <div className="grid items-center gap-12 lg:grid-cols-2">
          <div>
            <span className="ps-eyebrow">Inference · Serverless models</span>
            <h1 className="mt-4 text-[34px] font-medium leading-[1.1] sm:text-[44px] lg:text-[54px]">
              One API for <span className="text-ps-accent">frontier and open models</span>
            </h1>
            <p className="mt-6 max-w-lg text-[15px] leading-relaxed text-ps-muted">
              OpenAI-compatible. Token-level cost transparency. Pay in INR from your Podstack wallet.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <button onClick={() => redirectToCustomerLogin()} className="ps-btn">
                Get your API key <ArrowRightIcon className="h-4 w-4" />
              </button>
              <a href="#pricing" className="ps-btn2">View pricing</a>
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-3 text-[13px] text-ps-muted">
              <span className="flex items-center gap-2"><BoltIcon className="h-4 w-4 text-primary-400" /> Streaming + caching</span>
              <span className="flex items-center gap-2"><BanknotesIcon className="h-4 w-4 text-primary-400" /> Per-token INR pricing</span>
              <span className="flex items-center gap-2"><ShieldCheckIcon className="h-4 w-4 text-primary-400" /> Per-key limits &amp; data controls</span>
            </div>
          </div>
          <div>
            <CodeCard title="curl - inference" lines={SNIPPETS.curl.split('\n')} />
          </div>
        </div>
      </section>

      {/* Model showcase strip */}
      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6">
        <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
          {models.slice(0, 8).map((m) => (
            <div key={m.id} className="ps-card p-4">
              <div className="truncate text-sm font-medium text-ps-text">{m.display_name}</div>
              <div className="mt-1 text-xs text-ps-subtle">{(m.context_length / 1000).toFixed(0)}k context</div>
            </div>
          ))}
        </div>
      </section>

      {/* Recently added */}
      <section className="mx-auto max-w-7xl px-4 pb-16 sm:px-6">
        <RecentlyAdded models={catalogModels} onInfo={setDetailsEntry} />
      </section>

      {/* Pricing table */}
      <section id="pricing" className="mx-auto max-w-7xl scroll-mt-24 px-4 pb-16 sm:px-6">
        <h2 className="mb-5 text-2xl font-medium sm:text-[30px]">Pricing</h2>
        <CategoryTabs models={catalogModels} active={category} onChange={setCategory} />
        <div className="ps-card mt-4 overflow-hidden">
          {/* Desktop / tablet: compact table, all columns fit without scroll. */}
          <table className="ps-tbl hidden table-auto md:table">
            <thead>
              <tr>
                <th className="px-3 py-3 lg:px-4">Model</th>
                <th className="px-3 py-3 lg:px-4">Context</th>
                <th className="px-3 py-3 !text-right lg:px-4">Input / 1M tokens</th>
                <th className="px-3 py-3 !text-right lg:px-4">Output / 1M tokens</th>
                <th className="px-3 py-3 lg:px-4"><span className="sr-only">Details</span></th>
              </tr>
            </thead>
            <tbody>
              {displayedModels.map((m) => {
                const entry = catalogById.get(m.id);
                return (
                  <tr key={m.id}>
                    <td className="px-3 py-3 font-medium text-ps-text lg:px-4">{m.display_name}</td>
                    <td className="px-3 py-3 text-gray-400 lg:px-4">{(m.context_length / 1000).toFixed(0)}k</td>
                    <td className="whitespace-nowrap px-3 py-3 text-right font-mono text-gray-300 lg:px-4">
                      {m.pricing_available ? `₹${m.input_price_per_1m_inr.toFixed(2)}` : '-'}
                    </td>
                    <td className="whitespace-nowrap px-3 py-3 text-right font-mono text-gray-300 lg:px-4">
                      {m.pricing_available ? `₹${m.output_price_per_1m_inr.toFixed(2)}` : '-'}
                    </td>
                    <td className="px-3 py-3 text-right lg:px-4">
                      {entry && (
                        <button
                          aria-label={`About ${m.display_name}`}
                          onClick={() => setDetailsEntry(entry)}
                          className="p-1 rounded-md text-ps-subtle hover:text-ps-text hover:bg-ps-raised"
                        >
                          <InfoIcon />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {displayedModels.length === 0 && (
                <tr>
                  <td colSpan={5} className="px-4 py-8 text-center text-gray-500">
                    {models.length === 0 ? 'Model catalog loading…' : 'No models in this category yet.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>

          {/* Mobile: one card per model so every field is visible without scroll. */}
          <div className="divide-y divide-gray-800/50 md:hidden">
            {displayedModels.map((m) => {
              const entry = catalogById.get(m.id);
              return (
                <div key={m.id} className="p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="font-medium text-ps-text">{m.display_name}</div>
                    <div className="flex items-center gap-2">
                      <div className="whitespace-nowrap text-xs text-gray-500">{(m.context_length / 1000).toFixed(0)}k ctx</div>
                      {entry && (
                        <button
                          aria-label={`About ${m.display_name}`}
                          onClick={() => setDetailsEntry(entry)}
                          className="p-1 rounded-md text-ps-subtle hover:text-ps-text hover:bg-ps-raised"
                        >
                          <InfoIcon />
                        </button>
                      )}
                    </div>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-x-4 text-xs text-gray-400">
                    <div>
                      <dt className="inline text-gray-500">In: </dt>
                      <span className="font-mono text-gray-300">{m.pricing_available ? `₹${m.input_price_per_1m_inr.toFixed(2)}` : '-'}</span>
                      <span className="text-gray-600"> /1M</span>
                    </div>
                    <div>
                      <dt className="inline text-gray-500">Out: </dt>
                      <span className="font-mono text-gray-300">{m.pricing_available ? `₹${m.output_price_per_1m_inr.toFixed(2)}` : '-'}</span>
                      <span className="text-gray-600"> /1M</span>
                    </div>
                  </div>
                </div>
              );
            })}
            {displayedModels.length === 0 && (
              <div className="p-8 text-center text-gray-500">
                {models.length === 0 ? 'Model catalog loading…' : 'No models in this category yet.'}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Quickstart */}
      <section className="mx-auto max-w-7xl px-4 pb-24 sm:px-6">
        <h2 className="mb-5 text-2xl font-medium sm:text-[30px]">Quickstart</h2>
        <div className="ps-card flex flex-col gap-3 p-5">
          <div className="ps-seg self-start" role="group" aria-label="Language">
            {(['curl', 'python', 'javascript'] as const).map((l) => (
              <button
                key={l}
                onClick={() => setLang(l)}
                aria-pressed={lang === l}
                className={lang === l ? 'ps-seg-on' : ''}
              >
                {l}
              </button>
            ))}
          </div>
          <pre className="ps-pre">{SNIPPETS[lang]}</pre>
        </div>
      </section>

      <footer className="border-t border-ps-raised bg-ps-chrome py-10 text-center text-[13px] text-ps-subtle">
        © {new Date().getFullYear()} PodStack - Inference Cloud ·{' '}
        <a href="https://podstack.ai" className="ps-link">podstack.ai</a>
      </footer>

      <CompareTray
        models={catalogModels}
        selection={compareSelection}
        onRemove={handleCompareToggle}
        onCompare={() => setCompareOpen(true)}
        onClear={() => setCompareSelection([])}
      />

      {detailsEntry && (
        <ModelDetailsModal
          entry={detailsEntry}
          currency={currency}
          onClose={() => setDetailsEntry(null)}
          primaryCta={{ label: 'Sign in to try', to: '/login' }}
          extraActions={
            <button
              className="ps-btn2 ps-btn-sm"
              onClick={() => {
                handleCompareToggle(detailsEntry.id);
                setDetailsEntry(null);
              }}
            >
              Add to compare
            </button>
          }
        />
      )}

      {compareOpen && compareA && compareB && (
        <LandingCompareModal a={compareA} b={compareB} currency={currency} onClose={() => setCompareOpen(false)} />
      )}
    </div>
  );
};

export default Landing;
